import { useMemo } from 'react'
import Link from '@/routing/Link'
import { useRouter } from '@/routing/navigation'
import {
  Menu,
  Settings,
  Sun,
  Moon,
} from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { UiLanguageMenuItems } from '@/components/auth/UiLanguageMenuItems'
import { NotificationCenter } from '@/components/notifications/NotificationCenter'
import { cn, capitalizeFirst } from '@/lib/utils'
import { useProfile } from '@/lib/hooks'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useAuth } from '@/lib/config/auth-provider'
import { useTheme } from '@/lib/theme-provider'
import { useBrand } from '@/lib/brand-provider'
import { getUserInitials } from '@/lib/utils/avatar'
import { UserAvatar } from '@/components/ui/UserAvatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface MobileNavBarProps {
  onOpenMenu: () => void
}


export function MobileNavBar({ onOpenMenu }: MobileNavBarProps) {
  const { t } = useTranslation('app')
  const router = useRouter()
  const { user: authUser, logout } = useAuth()
  const { data: profile } = useProfile()
  const { org } = useCurrentOrg()
  const { brandSettings, brandLogoUrlOnDark, brandIconLogoUrl } = useBrand()
  const { resolvedTheme, toggleTheme } = useTheme()
  // structural only: Sun/Moon icon swap and "Light/Dark mode" label.
  const isDark = resolvedTheme === 'dark'

  const whiteLabelEnabled = (authUser?.featureFlags as { whiteLabel?: boolean } | undefined)?.whiteLabel === true
  const orgName = org?.name?.trim() || authUser?.companyName?.trim() || null
  const effectiveWhiteLabel = whiteLabelEnabled
  const brandIconSrc = effectiveWhiteLabel
    ? brandIconLogoUrl
    : '/sidebar-brand-logo.svg'
  const orgInitial = orgName ? (Array.from(orgName.trim())[0]?.toUpperCase() ?? null) : null
  const resolvedWordmarkUrl = effectiveWhiteLabel
    ? (isDark
        ? (brandLogoUrlOnDark || brandSettings.brandLogoUrl)
        : (brandSettings.brandLogoUrl || brandLogoUrlOnDark)) || null
    : null
  const wordmarkIsDuplicateOfIcon =
    resolvedWordmarkUrl !== null &&
    brandIconLogoUrl !== null &&
    resolvedWordmarkUrl === brandIconLogoUrl
  const brandWordmarkSrc = wordmarkIsDuplicateOfIcon ? null : resolvedWordmarkUrl
  // Mobile chrome is too narrow for icon + wordmark. Show one mark:
  // org logo when uploaded, otherwise the centered icon (Fairgen default or org icon).
  const showWordmarkImage = Boolean(brandWordmarkSrc)
  const showOrgNameAsLogo = effectiveWhiteLabel && !showWordmarkImage && !brandIconSrc && Boolean(orgName)
  const showIcon = !showWordmarkImage && !showOrgNameAsLogo
  const brandLinkLabel = effectiveWhiteLabel ? (orgName || 'Home') : 'Fairgen'
  const brandWordmarkAlt = orgName || 'Brand'

  const avatarSeed = profile?.avatarSeed ?? profile?.email ?? profile?.name ?? 'user'
  const avatarInitials = useMemo(
    () => getUserInitials(profile?.name, profile?.email),
    [profile?.name, profile?.email],
  )
  const userName = profile?.name
    ? profile.name.split(' ').map((part: string) => capitalizeFirst(part)).join(' ')
    : 'User'

  const dropdownItemClass = cn(
    'flex items-center gap-3 p-3 text-sm transition-colors cursor-pointer',
    'text-sidebar-foreground/80 hover:text-sidebar-foreground focus:text-sidebar-foreground',
    'hover:bg-[var(--sidebar-popover-item-hover-bg)] focus:bg-[var(--sidebar-popover-item-hover-bg)]',
  )

  return (
    <div className="md:hidden sticky top-0 z-50 flex h-14 w-full items-center justify-between bg-background px-3">
      {/* Left: hamburger */}
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label="Open navigation menu"
        data-testid="mobile-navbar-open-menu"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-accent"
      >
        <Menu className="h-5 w-5 text-foreground" strokeWidth={1.5} />
      </button>

      {/* Center: logo (absolutely centered so it stays centered regardless of right-side width) */}
      <Link
        href="/"
        aria-label={brandLinkLabel}
        data-testid="mobile-navbar-brand-home-link"
        className="absolute left-1/2 -translate-x-1/2 flex items-center"
      >
        {showIcon && brandIconSrc ? (
          <img
            src={brandIconSrc}
            alt=""
            aria-hidden="true"
            data-testid="mobile-navbar-brand-logo"
            className="h-7 w-[29px] object-contain"
          />
        ) : null}
        {showIcon && !brandIconSrc && orgInitial ? (
          <span
            aria-hidden="true"
            data-testid="mobile-navbar-brand-logo"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-current bg-transparent text-[13px] font-semibold text-sidebar-foreground"
          >
            {orgInitial}
          </span>
        ) : null}
        {showWordmarkImage && brandWordmarkSrc ? (
          <img
            src={brandWordmarkSrc}
            alt={brandWordmarkAlt}
            data-testid="mobile-navbar-brand-wordmark"
            className="h-7 max-w-[140px] object-contain"
          />
        ) : null}
        {showOrgNameAsLogo && orgName ? (
          <span
            data-testid="mobile-navbar-brand-wordmark"
            className="max-w-[140px] truncate text-sm font-semibold text-sidebar-foreground"
          >
            {orgName}
          </span>
        ) : null}
      </Link>

      {/* Right: notifications + profile */}
      <div className="flex shrink-0 items-center gap-0.5">
        <NotificationCenter className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground hover:bg-accent" />
        {/* Profile dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`${userName} menu`}
              data-testid="mobile-navbar-user-menu"
              className="flex h-9 w-9 items-center justify-center rounded-lg transition-colors hover:bg-accent"
            >
              <UserAvatar
                initials={avatarInitials}
                name={userName}
                size="sm"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className={cn(
              'w-56 bg-background',
              'border-[var(--sidebar-popover-border)]',
            )}
          >
            <div className="border-b border-sidebar-border/20 px-3 py-2">
              <p className="truncate text-sm font-semibold text-sidebar-foreground">{userName}</p>
            </div>
            <DropdownMenuItem
              onClick={() => router.push('/settings')}
              className={dropdownItemClass}
            >
              <Settings className="h-4 w-4" strokeWidth={1.5} />
              <span>{t('nav.settings')}</span>
            </DropdownMenuItem>
            <UiLanguageMenuItems itemClassName={dropdownItemClass} />
            <DropdownMenuItem
              onClick={(event) => {
                event.preventDefault()
                toggleTheme()
              }}
              className={dropdownItemClass}
            >
              {isDark ? (
                <Sun className="h-4 w-4" strokeWidth={1.5} />
              ) : (
                <Moon className="h-4 w-4" strokeWidth={1.5} />
              )}
              <span>{isDark ? t('nav.lightMode') : t('nav.darkMode')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-sidebar-border/20 dark:bg-[hsl(var(--muted))]" />
            <DropdownMenuItem onClick={() => logout()} className={dropdownItemClass}>
              <span>{t('nav.signOut')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
